import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, PencilSimple, ArrowUp, ArrowDown, ClockCounterClockwise, Warning, MagnifyingGlass, DownloadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { DisposalRequestsDialog } from "@/components/DisposalRequestsDialog";
import { downloadFile } from "@/lib/api";

const CATEGORIES = [
  { key: "spare_parts",     label: "Spare Parts" },
  { key: "office_supplies", label: "Office Supplies" },
  { key: "it_equipment",    label: "IT Equipment" },
];

const CONDITIONS = [
  { key: "new",         label: "New",         tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "used",        label: "Used",        tone: "bg-blue-50 text-blue-800 border-blue-200" },
  { key: "refurbished", label: "Refurbished", tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { key: "disposal",    label: "For Disposal", tone: "bg-rose-50 text-rose-800 border-rose-200" },
];
const CONDITION_TONE = Object.fromEntries(CONDITIONS.map((c) => [c.key, c]));

const EMPTY = {
  name: "", sku: "", category: "spare_parts", condition: "new", unit: "pcs", location: "",
  unit_cost: 0, reorder_point: 0, supplier_id: "", supplier_name: "", notes: "",
};

export default function InventoryPage() {
  const { user } = useAuth();
  const canEdit = can(user, ["admin", "procurement", "inventory"]);
  const [category, setCategory] = useState("spare_parts");
  const [condition, setCondition] = useState("all");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  // item form
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // movement form
  const [moving, setMoving] = useState(null);            // {item, type}
  const [mv, setMv] = useState({ quantity: "", reason: "", reference_no: "" });

  // movements history
  const [history, setHistory] = useState(null);          // {item, rows}

  // disposal clearance
  const [disposing, setDisposing] = useState(null);      // item
  const [dr, setDr] = useState({ quantity: "", reason: "" });
  const [drOpen, setDrOpen] = useState(false);
  const [pendingDr, setPendingDr] = useState(0);
  const isAdmin = user?.role === "admin";

  // mark units for disposal
  const [marking, setMarking] = useState(null);          // item
  const [mk, setMk] = useState({ quantity: "", note: "" });
  const disposableOf = (it) => (it.disposal_qty > 0 ? Math.min(it.disposal_qty, it.quantity || 0) : it.condition === "disposal" ? (it.quantity || 0) : 0);

  async function submitMark(e) {
    e.preventDefault();
    const qty = parseInt(mk.quantity);
    if (Number.isNaN(qty) || qty < 0) { toast.error("Enter 0 or a positive number of units"); return; }
    try {
      await api.post(`/inventory/${marking.id}/mark-disposal`, { quantity: qty, note: mk.note || null });
      toast.success(qty === 0 ? "Disposal mark cleared" : `${qty} ${marking.unit} marked for disposal`);
      window.dispatchEvent(new Event("notices:refresh"));
      setMarking(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function loadPendingDr() {
    try { const { data } = await api.get("/disposal-requests", { params: { status: "pending" } }); setPendingDr(data.length); } catch { /* ignore */ }
  }

  async function submitDisposal(e) {
    e.preventDefault();
    const qty = parseInt(dr.quantity);
    if (!qty || qty <= 0) { toast.error("Quantity must be a positive integer"); return; }
    try {
      await api.post(`/inventory/${disposing.id}/disposal-requests`, { quantity: qty, reason: dr.reason || null });
      toast.success("Disposal clearance requested — awaiting admin approval");
      window.dispatchEvent(new Event("notices:refresh"));
      setDisposing(null); setDr({ quantity: "", reason: "" });
      load(); loadPendingDr();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed"); }
  }

  async function load() {
    setLoading(true);
    try {
      const params = { category, ...(search ? { search } : {}), ...(condition !== "all" ? { condition } : {}) };
      const { data } = await api.get("/inventory", { params });
      setItems(data);
    } finally { setLoading(false); }
  }

  async function loadSuppliers() {
    try { const { data } = await api.get("/suppliers"); setSuppliers(data); } catch { /* ignore */ }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, search, condition]);
  useEffect(() => { loadSuppliers(); loadPendingDr(); }, []);

  function openCreate() { setEditing(null); setForm({ ...EMPTY, category }); setOpen(true); }
  function openEdit(it) {
    setEditing(it);
    setForm({
      name: it.name || "", sku: it.sku || "", category: it.category, condition: it.condition || "new",
      unit: it.unit || "pcs", location: it.location || "",
      unit_cost: it.unit_cost ?? 0, reorder_point: it.reorder_point ?? 0,
      supplier_id: it.supplier_id || "", supplier_name: it.supplier_name || "",
      notes: it.notes || "",
    });
    setOpen(true);
  }

  async function submitItem(e) {
    e.preventDefault();
    try {
      const payload = { ...form, unit_cost: Number(form.unit_cost) || 0, reorder_point: parseInt(form.reorder_point) || 0 };
      if (editing) { await api.put(`/inventory/${editing.id}`, payload); toast.success("Item updated"); }
      else { await api.post("/inventory", payload); toast.success("Item added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
  }

  async function submitMovement(e) {
    e.preventDefault();
    if (!moving) return;
    const qty = parseInt(mv.quantity);
    if (!qty || qty <= 0) { toast.error("Quantity must be a positive integer"); return; }
    try {
      await api.post(`/inventory/${moving.item.id}/movements`, {
        type: moving.type,
        quantity: qty,
        reason: mv.reason || null,
        reference_no: mv.reference_no || null,
      });
      toast.success(`Stock ${moving.type === "in" ? "in" : moving.type === "out" ? "out" : "adjusted"} — ${qty} ${moving.item.unit}`);
      window.dispatchEvent(new Event("notices:refresh"));
      setMoving(null); setMv({ quantity: "", reason: "", reference_no: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Movement failed"); }
  }

  async function openHistory(it) {
    try {
      const { data } = await api.get(`/inventory/${it.id}/movements`);
      setHistory({ item: it, rows: [...data].reverse() });
    } catch { toast.error("Failed to load history"); }
  }

  const catLabel = useMemo(() => CATEGORIES.find((c) => c.key === category)?.label || "Inventory", [category]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Inventory</div>
          <h1 className="section-title text-3xl sm:text-4xl">{catLabel}</h1>
          <p className="text-sm text-muted-foreground mt-1">Track stock levels, log movements and get low-stock alerts.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-border" onClick={() => setDrOpen(true)} data-testid="disposal-requests-btn">
              <Trash size={14} className="mr-2" /> Disposal Requests
              {pendingDr > 0 && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white tabular-nums" data-testid="disposal-pending-badge">{pendingDr}</span>}
            </Button>
            <Button variant="outline" className="border-border" onClick={async () => {
              try {
                const stamp = new Date().toISOString().slice(0, 10);
                await downloadFile(`/inventory/export/xlsx?category=${category}`, `inventory_${category}_${stamp}.xlsx`);
                toast.success("Exported for audit");
              } catch { toast.error("Export failed"); }
            }} data-testid="inventory-export-current-btn">
              <DownloadSimple size={14} className="mr-2" /> Export {catLabel}
            </Button>
            <Button variant="outline" className="border-border" onClick={async () => {
              try {
                const stamp = new Date().toISOString().slice(0, 10);
                await downloadFile(`/inventory/export/xlsx`, `inventory_all_${stamp}.xlsx`);
                toast.success("Exported all categories");
              } catch { toast.error("Export failed"); }
            }} data-testid="inventory-export-all-btn">
              <DownloadSimple size={14} className="mr-2" /> Export All
            </Button>
            <Button onClick={openCreate} data-testid="new-inventory-btn"><Plus size={16} className="mr-2" /> New Item</Button>
          </div>
        )}
        {!canEdit && (
          <Button variant="outline" className="border-border" onClick={async () => {
            try {
              const stamp = new Date().toISOString().slice(0, 10);
              await downloadFile(`/inventory/export/xlsx?category=${category}`, `inventory_${category}_${stamp}.xlsx`);
              toast.success("Exported for audit");
            } catch { toast.error("Export failed"); }
          }} data-testid="inventory-export-current-btn">
            <DownloadSimple size={14} className="mr-2" /> Export {catLabel}
          </Button>
        )}
      </div>

      <Tabs value={category} onValueChange={setCategory}>
        <TabsList className="border border-border">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={`inventory-tab-${c.key}`}>{c.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SKU, location, supplier…"
            className="pl-9 border-border"
            data-testid="inventory-search-input"
          />
        </div>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger className="w-[180px] border-border" data-testid="inventory-condition-filter"><SelectValue placeholder="All conditions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All conditions</SelectItem>
            {CONDITIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1200px]">
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Condition</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">On Hand</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Unit Cost</TableHead>
              <TableHead className="text-right">Reorder</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No items in {catLabel} yet.</TableCell></TableRow>
            ) : items.map((it) => {
              const low = (it.quantity || 0) <= (it.reorder_point || 0);
              const cond = CONDITION_TONE[it.condition || "new"] || CONDITION_TONE.new;
              return (
                <TableRow key={it.id} data-testid={`inventory-row-${it.id}`}>
                  <TableCell className="font-mono text-xs">{it.sku || "—"}</TableCell>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${cond.tone}`}>{cond.label}</span>
                    {disposableOf(it) > 0 && it.condition !== "disposal" && (
                      <div className="text-[10px] uppercase tracking-widest mt-1 text-rose-700" data-testid={`disposal-units-${it.id}`}>
                        {disposableOf(it)} of {it.quantity || 0} {it.unit} for disposal
                      </div>
                    )}
                    {disposableOf(it) > 0 && it.disposal_status && (
                      <div className={`text-[10px] uppercase tracking-widest mt-1 ${it.disposal_status === "cleared" ? "text-emerald-700" : it.disposal_status === "pending" ? "text-amber-700" : "text-rose-700"}`} data-testid={`disposal-status-label-${it.id}`}>
                        {it.disposal_status === "cleared" ? "Cleared for disposal" : it.disposal_status === "pending" ? "Clearance pending" : "Clearance rejected"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{it.location || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{it.supplier_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    <span className={low ? "inline-flex items-center gap-1 text-rose-700" : ""}>
                      {low && <Warning size={14} weight="fill" />}
                      {it.quantity ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{it.unit}</TableCell>
                  <TableCell className="text-right tabular-nums">₱{Number(it.unit_cost || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{it.reorder_point || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {canEdit && (
                        <>
                          <Button size="sm" variant="outline" className="border-border" onClick={() => { setMoving({ item: it, type: "in" }); setMv({ quantity: "", reason: "", reference_no: "" }); }} data-testid={`stock-in-${it.id}`}>
                            <ArrowUp size={14} className="mr-1 text-emerald-700" /> In
                          </Button>
                          <Button size="sm" variant="outline" className="border-border" onClick={() => { setMoving({ item: it, type: "out" }); setMv({ quantity: "", reason: "", reference_no: "" }); }} data-testid={`stock-out-${it.id}`}>
                            <ArrowDown size={14} className="mr-1 text-rose-700" /> Out
                          </Button>
                          <Button size="sm" variant="outline" className="border-border" title="Mark units for disposal"
                            onClick={() => { setMarking(it); setMk({ quantity: String(disposableOf(it) || ""), note: "" }); }}
                            data-testid={`mark-disposal-${it.id}`}>
                            <Trash size={14} className="text-rose-700" />
                          </Button>
                          {disposableOf(it) > 0 && (
                            <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                              disabled={it.disposal_status === "pending"}
                              onClick={() => { setDisposing(it); setDr({ quantity: String(disposableOf(it)), reason: "" }); }}
                              data-testid={`disposal-request-${it.id}`}>
                              <Trash size={14} className="mr-1" /> {it.disposal_status === "pending" ? "Pending" : `Clearance (${disposableOf(it)})`}
                            </Button>
                          )}
                        </>
                      )}
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openHistory(it)} data-testid={`history-${it.id}`}>
                        <ClockCounterClockwise size={14} />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(it)} data-testid={`edit-inventory-${it.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      {canEdit && (
                        <ArchiveButton endpoint={`/inventory/${it.id}`} label={it.name} onArchived={load} testId={`archive-inventory-${it.id}`} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit item */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="inventory-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? `Edit ${editing.name}` : "New Item"}</DialogTitle></DialogHeader>
          <form onSubmit={submitItem} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-border" data-testid="inventory-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">SKU</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="border-border" data-testid="inventory-sku-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="inventory-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Condition *</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger data-testid="inventory-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="border-border" data-testid="inventory-unit-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border-border" data-testid="inventory-location-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Unit Cost (₱)</Label>
                <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="border-border" data-testid="inventory-cost-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reorder Point</Label>
                <Input type="number" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} className="border-border" data-testid="inventory-reorder-input" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Supplier</Label>
                <Select value={form.supplier_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setForm({ ...form, supplier_id: "", supplier_name: "" }); return; }
                  const s = suppliers.find((x) => x.id === v);
                  setForm({ ...form, supplier_id: v, supplier_name: s?.name || "" });
                }}>
                  <SelectTrigger data-testid="inventory-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" data-testid="inventory-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="inventory-submit-btn">{editing ? "Save" : "Add item"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock movement */}
      <Dialog open={!!moving} onOpenChange={(v) => !v && setMoving(null)}>
        <DialogContent data-testid="stock-movement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">
              Stock {moving?.type === "in" ? "In" : "Out"} — {moving?.item?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitMovement} className="space-y-3">
            <div className="p-3 border border-border rounded-md bg-muted/30 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Current on hand</span><span className="font-semibold tabular-nums">{moving?.item?.quantity ?? 0} {moving?.item?.unit}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Quantity *</Label>
              <Input type="number" min="1" required value={mv.quantity} onChange={(e) => setMv({ ...mv, quantity: e.target.value })} className="border-border" data-testid="movement-quantity-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
              <Input value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} placeholder={moving?.type === "in" ? "e.g. Purchase received" : "e.g. Issued to Ops"} className="border-border" data-testid="movement-reason-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reference No.</Label>
              <Input value={mv.reference_no} onChange={(e) => setMv({ ...mv, reference_no: e.target.value })} placeholder="PO / Request / manual note" className="border-border" data-testid="movement-reference-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setMoving(null)}>Cancel</Button>
              <Button type="submit" data-testid="movement-submit-btn">Record {moving?.type === "in" ? "Stock In" : "Stock Out"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark units for disposal */}
      <Dialog open={!!marking} onOpenChange={(v) => !v && setMarking(null)}>
        <DialogContent data-testid="mark-disposal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Mark for Disposal — {marking?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitMark} className="space-y-3">
            <div className="p-3 border border-border rounded-md bg-muted/30 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">On hand</span><span className="font-semibold tabular-nums">{marking?.quantity ?? 0} {marking?.unit}</span></div>
              <div className="flex justify-between mt-1"><span className="text-muted-foreground">Currently marked</span><span className="font-semibold tabular-nums">{marking ? disposableOf(marking) : 0} {marking?.unit}</span></div>
              <p className="text-xs text-muted-foreground mt-2">Only the units you enter are treated as disposal stock; the rest stay usable. Enter 0 to clear the mark.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Units for disposal *</Label>
              <Input type="number" min="0" max={marking?.quantity || 0} required value={mk.quantity} onChange={(e) => setMk({ ...mk, quantity: e.target.value })} className="border-border" data-testid="mark-disposal-quantity-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
              <Input value={mk.note} onChange={(e) => setMk({ ...mk, note: e.target.value })} placeholder="e.g. 3 units damaged in transit" className="border-border" data-testid="mark-disposal-note-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setMarking(null)}>Cancel</Button>
              <Button type="submit" data-testid="mark-disposal-submit-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Disposal clearance request */}
      <Dialog open={!!disposing} onOpenChange={(v) => !v && setDisposing(null)}>
        <DialogContent data-testid="disposal-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Request Disposal Clearance — {disposing?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDisposal} className="space-y-3">
            <div className="p-3 border border-border rounded-md bg-muted/30 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Units marked for disposal</span><span className="font-semibold tabular-nums">{disposing ? disposableOf(disposing) : 0} of {disposing?.quantity ?? 0} {disposing?.unit}</span></div>
              <p className="text-xs text-muted-foreground mt-2">Requires Admin approval. Once approved, a Stock Out is recorded automatically and the item is marked cleared.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Quantity to dispose *</Label>
              <Input type="number" min="1" max={disposing ? disposableOf(disposing) : 1} required value={dr.quantity} onChange={(e) => setDr({ ...dr, quantity: e.target.value })} className="border-border" data-testid="disposal-quantity-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
              <Textarea rows={2} value={dr.reason} onChange={(e) => setDr({ ...dr, reason: e.target.value })} placeholder="e.g. Beyond repair, obsolete model" className="border-border" data-testid="disposal-reason-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDisposing(null)}>Cancel</Button>
              <Button type="submit" data-testid="disposal-submit-btn">Submit for Approval</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DisposalRequestsDialog open={drOpen} onOpenChange={setDrOpen} isAdmin={isAdmin} onDecided={() => { load(); loadPendingDr(); }} />

      {/* Movement history */}
      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-2xl" data-testid="movement-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Movement History — {history?.item?.name}</DialogTitle>
          </DialogHeader>
          <div className="border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(history?.rows || []).length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No movements yet.</TableCell></TableRow>
                ) : history.rows.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-mono text-xs">{(m.occurred_at || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded border ${m.type === "in" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : m.type === "out" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                        {m.type === "in" ? "STOCK IN" : m.type === "out" ? "STOCK OUT" : "ADJUSTMENT"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{m.quantity}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.reason || m.reference_no || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{m.balance_after}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.created_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
